import type { LegendListRef } from '@legendapp/list/react-native';
import type { FC, Ref } from 'react';
import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetPostsByGeoArea } from '@/features/api/hooks/grapevine-post/useGetPostsByGeoArea';
import { MeineRegionCreatePostFab } from '@/features/geo-area/components/region/MeineRegionCreatePostFab';
import { REGION_FILTER_HEIGHT } from '@/features/geo-area/components/region/RegionFilterDropdown';
import {
  RegionNewsTicker,
  TICKER_HEIGHT
} from '@/features/geo-area/components/region/RegionNewsTicker';
import { useRegionTab } from '@/features/geo-area/components/region/RegionTab';
import { RegionTab } from '@/features/geo-area/components/region/regionTypes';
import type { useMeineRegionPostFeed } from '@/features/geo-area/hooks/useMeineRegionPostFeed';
import { PostFeed } from '@/features/posts/components/PostFeed';
import { Expand } from '@/shared/components/layout/Expand';
import { useBadgeStore } from '@/shared/store/useBadgeStore';

type Props = {
  feed: ReturnType<typeof useMeineRegionPostFeed>;
  postFeedRef: Ref<LegendListRef>;
  rootGeoAreaId?: string;
};

export const MeineRegionBody: FC<Props> = ({
  feed,
  postFeedRef,
  rootGeoAreaId
}) => {
  const { activeTab, scrollY, feedInset } = useRegionTab();
  const insets = useSafeAreaInsets();
  const { data: tickerPosts, isSuccess } = useGetPostsByGeoArea({
    geoAreaId: rootGeoAreaId
  });
  const hasTicker = (tickerPosts?.length ?? 0) > 0;
  const hasFilter =
    activeTab === RegionTab.EVENTS ||
    activeTab === RegionTab.REGIONAL_RADIO ||
    activeTab === RegionTab.EMERGENCY;
  const filterHeight = hasFilter ? REGION_FILTER_HEIGHT : 0;

  const markPostsAsRead = useBadgeStore((state) => state.markPostsAsRead);
  const activePosts = feed.query.data;
  useEffect(() => {
    if (!activePosts?.length) return;
    markPostsAsRead(activePosts.map((p) => p.postId));
  }, [activePosts, markPostsAsRead]);

  return (
    <>
      <Expand>
        <PostFeed params={feed.params} query={feed.query}>
          <PostFeed.Loading />
          <PostFeed.Error />
          <View
            style={{
              flex: 1,
              transform: [{ translateY: filterHeight }]
            }}
          >
            <PostFeed.List
              ref={postFeedRef}
              scrollOffset={scrollY}
              hidePostType={activeTab === RegionTab.REGIONAL_RADIO}
              contentContainerStyle={{
                paddingTop: feedInset,
                paddingBottom:
                  insets.bottom + (hasTicker ? TICKER_HEIGHT : 0) + filterHeight
              }}
            />
          </View>
        </PostFeed>
      </Expand>
      <MeineRegionCreatePostFab bottom={hasTicker ? TICKER_HEIGHT : 0} />
      <View style={styles.ticker}>
        <RegionNewsTicker posts={tickerPosts} isSuccess={isSuccess} />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  ticker: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 4
  }
});
